import { useCallback, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient, type QueryClient, type QueryKey } from '@tanstack/react-query';
import type { StorageOperationError, StorageQueueMessage, StorageResourceRef } from '../../../../types/azureStorage';
import { toStorageOperationError } from '../services/azureStorageApi';
import { storageQueuePeekQueryKey } from '../services/storageListingKeys';
import { useStorageReconnectRefetch } from '../hooks/useStorageTabConnection';
import { useStorageExplorerStore } from '../store/storageExplorerStore';
import { startQueuePeek } from './queueApi';
import { queueRowsFor, receivedReceiptHandles, useQueueMessagesStore } from './queueMessagesStore';

type QueueRef = Extract<StorageResourceRef, { kind: 'queue' }>;
type Args = { tabId: string; connectionId: string; resource: QueueRef; count: number; decodeBase64: boolean; enabled: boolean };
type PeekAnswer = { requestId: string; messages: StorageQueueMessage[]; approximateMessageCount?: number; connectionGeneration: number; staleReceipts?: { removed: string[]; lost: string[] } };

/** Shows the peeked set and re-runs the tab's peek query; rejects with the query error. */
export async function peekQueueAgain(queryClient: QueryClient, tabId: string, queryKey: QueryKey): Promise<void> {
  useQueueMessagesStore.getState().showPeeked(tabId);
  await queryClient.resetQueries({ queryKey, exact: true });
  const error = queryClient.getQueryState(queryKey)?.error;
  if (error) throw error;
}

export function useQueuePeek({ tabId, connectionId, resource, count, enabled }: Args) {
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => storageQueuePeekQueryKey(connectionId, resource, count), [connectionId, resource.queue, count]);
  const runtime = useQueueMessagesStore((state) => state.byTab[tabId]);

  useEffect(() => {
    useStorageExplorerStore.getState().ensure(tabId, connectionId);
    useQueueMessagesStore.getState().ensure(tabId, connectionId);
  }, [tabId, connectionId]);

  const query = useQuery({
    queryKey,
    enabled,
    retry: false,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    queryFn: async ({ signal }): Promise<PeekAnswer> => {
      // Received rows ride along so a Clear/delete from any surface (tree, other tab, MCP) retires them here too.
      const receiptHandles = receivedReceiptHandles(useQueueMessagesStore.getState().byTab[tabId]);
      const handle = startQueuePeek(tabId, { connectionId, resource, count, timeoutMs: 30_000, ...(receiptHandles.length > 0 ? { receiptHandles } : {}) });
      useQueueMessagesStore.getState().beginPeek(tabId, handle.requestId);
      const onAbort = () => handle.cancel();
      signal.addEventListener('abort', onAbort);
      try {
        const result = await handle.promise;
        if (!result.ok) throw result.error;
        return { requestId: handle.requestId, ...result.value };
      } finally {
        signal.removeEventListener('abort', onAbort);
      }
    },
  });

  useEffect(() => {
    if (!query.data) return;
    const status = useQueueMessagesStore.getState().applyPeek(tabId, query.data.requestId, query.data);
    if (status === 'generation-changed') void query.refetch();
  }, [query.data, tabId, query.refetch]);

  // Re-peek whenever the connection generation is replaced (shared with the blob tab): a peek recreated while disconnected
  // answered NotConnected, and an in-place Reconnect killed every receipt — the received set goes first (review F G1).
  // The old-generation peek is cancelled first: a bare refetch JOINS an in-flight fetch that has no data yet (the first
  // peek), which then settled with main's ConnectionRebuilt and never re-peeked (review G H1). The cancel aborts its
  // request and drops its late answer; settled rows stay shown while the fresh peek runs.
  const { refetch } = query;
  const onReconnect = useCallback(() => {
    useQueueMessagesStore.getState().connectionReplaced(tabId);
    void queryClient.cancelQueries({ queryKey, exact: true });
    void refetch();
  }, [queryClient, queryKey, refetch, tabId]);
  useStorageReconnectRefetch(connectionId, enabled, onReconnect);

  useEffect(() => () => {
    void queryClient.cancelQueries({ queryKey, exact: true });
    queryClient.removeQueries({ queryKey, exact: true });
  }, [queryClient, queryKey]);

  const peekAgain = useCallback(() => peekQueueAgain(queryClient, tabId, queryKey), [queryClient, queryKey, tabId]);

  return {
    messages: queueRowsFor(runtime),
    messageSet: runtime?.messageSet ?? 'peeked',
    peekEpoch: runtime?.peekEpoch ?? 0,
    approximateMessageCount: query.data?.approximateMessageCount,
    isLoading: query.isPending,
    isRefreshing: query.isFetching && !query.isPending,
    error: query.error ? toStorageOperationError(query.error) as StorageOperationError : undefined,
    peekAgain,
  };
}
